import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Alert, Button, FormField, Input, Surface } from '@nexvia-logistics/ui';
import { KeyValueListStacked } from '@/shared/KeyValueListStacked';
import { describeStorageProfileError, updateStorageProfile, type StorageProfile } from './storage-api';
import { SealCheckIcon } from '@/shared/icons';
import { isValidRut } from './storage-profile-fields';

export interface FiscalDataFormProps {
  profile: StorageProfile;
  onSaved: (updated: StorageProfile) => void;
}

/**
 * Audit S7 — the login email (the Firebase identity) and the address cannot be edited from the panel
 * (`PATCH /storage/me` has no field for either): the depósito asks support, the same address the
 * shell's "Ayuda y soporte" link uses.
 */
const EMAIL_CHANGE_HINT = 'Es tu correo de ingreso. Para cambiarlo, escribinos a soporte@nexvia.io.';
const ADDRESS_CHANGE_HINT = 'Para cambiarlo, escribinos a soporte@nexvia.io.';
const SUPPORT_CHANGE_HINT = 'Para cambiar tu correo de ingreso o tu domicilio, escribinos a soporte@nexvia.io.';

/**
 * Read-only presentation for an already-`verified` profile (fix #3, Claude Design mockup reskin):
 * term/value rows separated by a divider that the last row drops. NEX-1151: now the real
 * `@nexvia-logistics/ui` `KeyValueList` molecule — this hand-rolled `<dl>` (150px term column,
 * `dt`/`dd` pair, hairline rule between rows) was originally modeled on that molecule's shape as a
 * local stand-in because the package wasn't installed yet (same convention as the other former
 * stand-ins this app had); the package is real now, `KeyValueList.d.ts`/`.js` confirm
 * `items: [{term, value}]` is an exact match for `rows` below, so the stand-in is replaced
 * outright rather than kept alongside the real thing.
 *
 * Exactly the four rows the design shows — Razón social / RUT / Domicilio fiscal / Facturación —
 * not the editable form's "Correo" row too: the mockup for this verified state does not repeat it,
 * so this intentionally shows fewer fields than the editable form rather than a 1:1 superset of it.
 * `Domicilio fiscal`/`Facturación` stay conditional on `profile.address`/`profile.billsWithIva`
 * actually being present, same as the editable form already does (neither is a real backend column
 * yet — see this folder's `AGENTS.md`).
 *
 * NEX-1164 design pass (2026-10-18, spec §3.6, D-6.1): the mock's `SealCheck` in the title and
 * "Con IVA" / "Sin IVA" for Facturación. Same rows, same "Editar datos fiscales" (the mock is read
 * only; the edit stays because it exists and phase 1 asked for it).
 */
function FiscalDataSummary({ profile, onEdit }: { profile: StorageProfile; onEdit: () => void }) {
  const rows: Array<{ term: string; value: ReactNode }> = [
    { term: 'Razón social', value: profile.razonSocial },
    { term: 'RUT', value: profile.rut }
  ];
  if (profile.address !== undefined) {
    rows.push({ term: 'Domicilio fiscal', value: profile.address });
  }
  if (profile.billsWithIva !== undefined) {
    rows.push({ term: 'Facturación', value: profile.billsWithIva ? 'Con IVA' : 'Sin IVA' });
  }

  return (
    <Surface as="section" variant="outlined" padding="md">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 flex items-center gap-2 text-ui-h3 text-on-surface">
          <SealCheckIcon size={20} className="shrink-0 text-primary" />
          Datos fiscales
        </h2>
        {/* NEX-1164 (W2-hard-b) — the API lets a verified account edit these (a RUT or razón social
            change sends it back to review, which the editor warns about first). */}
        <Button type="button" variant="neutral" size="xs" showArrow={false} onClick={onEdit}>
          Editar datos fiscales
        </Button>
      </div>
      <KeyValueListStacked items={rows} className="mt-4" />
      <p className="mt-3 text-ui-small text-on-surface-placeholder">{SUPPORT_CHANGE_HINT}</p>
    </Surface>
  );
}

type FieldErrors = Partial<Record<'razonSocial' | 'rut' | 'phone', string>>;

/**
 * Exactly `update-storage-profile.handler.ts`'s own checks (empty razonSocial/phone, 12-digit
 * rut) — client-side only, for immediate feedback; the backend's codes
 * (`STORAGE_RUT_INVALID` et al., mapped in `describeStorageProfileError`) are what actually
 * decide a save, this only ever saves a round trip for the same three cases.
 */
export function validateFiscalData(input: { razonSocial: string; rut: string; phone: string }): FieldErrors {
  const errors: FieldErrors = {};
  if (!input.razonSocial.trim()) errors.razonSocial = 'La razón social no puede quedar vacía.';
  // NEX-1164 — the registration form's RUT rule (`storage-profile-fields.ts`, mirror of the API's).
  if (!isValidRut(input.rut)) errors.rut = 'El RUT tiene que tener exactamente 12 dígitos.';
  if (!input.phone.trim()) errors.phone = 'El teléfono no puede quedar vacío.';
  return errors;
}

/**
 * Razón social / RUT / teléfono (the only three `PATCH /storage/me` accepts), plus read-only
 * correo. `domicilio`/`facturación con IVA` render only when `profile` actually carries them —
 * see `storage-api.ts`'s own `[PENDING-DECISION]` on why neither is a real field yet.
 *
 * NEX-1164 (2026-10-15): `FormField`+`Input`; field errors live in `FormField`'s `error` slot
 * (`role="alert"`, wired to `aria-describedby`/`aria-invalid`), the backend error is an `Alert`
 * (spec D-6.2). Each `Input` also gets `invalid`: `FormField` only hands down `aria-invalid`, and
 * the field skin paints its critical ring from `invalid` alone — same composition as the package's
 * own `InlineEditRow`.
 */
/**
 * NEX-1164 (W2-hard-b) — the RUT and the razón social are what the DGI certificate backs: changing
 * either on a `verified` account sends it back to `pending_review` (nexvia-api-web
 * `services/account-data-review.ts`), and it cannot quote until Nexvia reviews it again. The save
 * asks first. Compared trimmed, as the API does (a re-save of the same value is not a change).
 */
function changesAccountData(profile: StorageProfile, razonSocial: string, rut: string): boolean {
  return razonSocial.trim() !== profile.razonSocial.trim() || rut.trim() !== profile.rut.trim();
}

const BACK_TO_REVIEW_MESSAGE =
  'Guardamos los cambios. Tu cuenta volvió a revisión: Nexvia tiene que revisar de nuevo tu certificado de DGI y, hasta que lo apruebe, no podés enviar cotizaciones.';

interface EditableFiscalDataFormProps extends FiscalDataFormProps {
  /** Back to the read-only summary; only offered while the account is verified. */
  onClose: () => void;
}

function EditableFiscalDataForm({ profile, onSaved, onClose }: EditableFiscalDataFormProps) {
  const [razonSocial, setRazonSocial] = useState(profile.razonSocial);
  const [rut, setRut] = useState(profile.rut);
  const [phone, setPhone] = useState(profile.phone);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmingReview, setConfirmingReview] = useState(false);
  const [backToReview, setBackToReview] = useState(false);

  // Resync local state whenever the canonical profile changes (initial load, or a sibling save
  // elsewhere) — not just on mount, so this form never shows stale values next to a fresh fetch.
  useEffect(() => {
    setRazonSocial(profile.razonSocial);
    setRut(profile.rut);
    setPhone(profile.phone);
  }, [profile.razonSocial, profile.rut, profile.phone]);

  const clearTransientState = () => {
    setSaved(false);
    setBackToReview(false);
    setConfirmingReview(false);
    setErrorMessage(null);
  };

  const save = async () => {
    setConfirmingReview(false);
    setSaving(true);
    setErrorMessage(null);
    try {
      const result = await updateStorageProfile({ razonSocial, rut, phone });
      const wasVerified = profile.approvalStatus === 'verified';
      const stillVerified = result.storageProfile.approvalStatus === 'verified';
      onSaved(result.storageProfile);
      if (wasVerified && stillVerified) {
        // A verified account editing only its phone: back to the summary, which shows the new value.
        onClose();
        return;
      }
      if (wasVerified) setBackToReview(true);
      else setSaved(true);
    } catch (err) {
      setErrorMessage(describeStorageProfileError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const errors = validateFiscalData({ razonSocial, rut, phone });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (profile.approvalStatus === 'verified' && changesAccountData(profile, razonSocial, rut)) {
      setConfirmingReview(true);
      return;
    }
    await save();
  };

  return (
    <Surface as="section" variant="outlined" padding="md">
      <h2 className="m-0 flex items-center gap-2 text-ui-h3 text-on-surface">
        <SealCheckIcon size={20} className="shrink-0 text-primary" />
        Datos fiscales
      </h2>

      <form onSubmit={(event) => void handleSubmit(event)} className="mt-4 flex flex-col gap-4" noValidate>
        <FormField label="Razón social" error={fieldErrors.razonSocial}>
          {(field) => (
            <Input
              {...field}
              invalid={Boolean(fieldErrors.razonSocial)}
              value={razonSocial}
              onChange={(event) => {
                setRazonSocial(event.target.value);
                clearTransientState();
              }}
            />
          )}
        </FormField>

        <FormField label="RUT" error={fieldErrors.rut}>
          {(field) => (
            <Input
              {...field}
              invalid={Boolean(fieldErrors.rut)}
              value={rut}
              inputMode="numeric"
              onChange={(event) => {
                setRut(event.target.value);
                clearTransientState();
              }}
            />
          )}
        </FormField>

        <FormField label="Teléfono" error={fieldErrors.phone}>
          {(field) => (
            <Input
              {...field}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              invalid={Boolean(fieldErrors.phone)}
              value={phone}
              onChange={(event) => {
                setPhone(event.target.value);
                clearTransientState();
              }}
            />
          )}
        </FormField>

        {/* Audit S7 — neither is editable here, so each says how to change it. */}
        <FormField label="Correo" hint={EMAIL_CHANGE_HINT}>
          {(field) => <Input {...field} readOnly value={profile.email} />}
        </FormField>

        {profile.address !== undefined && (
          <FormField label="Domicilio" hint={ADDRESS_CHANGE_HINT}>
            {(field) => <Input {...field} readOnly value={profile.address} />}
          </FormField>
        )}

        {profile.billsWithIva !== undefined && (
          <p className="m-0 text-ui-body text-on-surface-variant">{profile.billsWithIva ? 'Factura con IVA.' : 'No factura con IVA.'}</p>
        )}

        {errorMessage && <Alert tone="critical">{errorMessage}</Alert>}
        {saved && !errorMessage && (
          <p role="status" className="m-0 text-ui-body text-success-base">
            Cambios guardados.
          </p>
        )}
        {backToReview && !errorMessage && (
          <Alert tone="caution">
            <span role="status">{BACK_TO_REVIEW_MESSAGE}</span>
          </Alert>
        )}

        {confirmingReview ? (
          <Alert tone="caution">
            <div className="flex flex-col gap-3">
              <p className="m-0">
                Si cambiás el RUT o la razón social, tu cuenta vuelve a revisión: Nexvia tiene que revisar de nuevo tu
                certificado de DGI y, hasta que lo apruebe, no vas a poder enviar cotizaciones. ¿Querés guardar igual?
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="primary" size="xs" showArrow={false} onClick={() => void save()}>
                  Guardar y enviar a revisión
                </Button>
                <Button type="button" variant="text" size="xs" showArrow={false} onClick={() => setConfirmingReview(false)}>
                  Seguir editando
                </Button>
              </div>
            </div>
          </Alert>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" size="xs" showArrow={false} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
            {profile.approvalStatus === 'verified' && (
              <Button type="button" variant="text" size="xs" showArrow={false} onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
            )}
          </div>
        )}
      </form>
    </Surface>
  );
}

/**
 * Switches between the two presentations above by `profile.approvalStatus` (fix #3, Claude Design
 * mockup reskin): read-only `FiscalDataSummary` once `verified`, the editable form for every other
 * state (`incomplete`/`pending_review`/`rejected`/`suspended`) — unchanged behavior for all of
 * those. Each branch is its own full component, not a conditional inside one, so this switch is
 * not a rules-of-hooks violation: `EditableFiscalDataForm`'s hooks still only ever run inside
 * `EditableFiscalDataForm` itself, unconditionally.
 */
export function FiscalDataForm({ profile, onSaved }: FiscalDataFormProps) {
  // NEX-1164 (W2-hard-b) — a verified account opens the editor from its summary.
  const [editing, setEditing] = useState(false);
  if (profile.approvalStatus === 'verified' && !editing) {
    return <FiscalDataSummary profile={profile} onEdit={() => setEditing(true)} />;
  }
  return <EditableFiscalDataForm profile={profile} onSaved={onSaved} onClose={() => setEditing(false)} />;
}
